import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';

interface ModalSignPinProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (pin: string, reason?: string) => Promise<void>;
  fieldLabel: string;
  reasonRequired?: boolean;
  title?: string;
  submitLabel?: string;
}

const ModalSignPin: React.FC<ModalSignPinProps> = ({ isOpen, onClose, onConfirm, fieldLabel, reasonRequired = false, title, submitLabel }) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError(null);
      setLoading(false);
      setReason('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin || (reasonRequired && !reason.trim()) || loading) return;
    setError(null);
    setLoading(true);
    try {
      await onConfirm(pin, reason.trim() || undefined);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        setError(axiosErr.response?.data?.message || t('doc.incorrectPin'));
      } else {
        setError(t('doc.incorrectPin'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-80">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {title || t('doc.signDocument')} — {fieldLabel}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('doc.enterPin')}
          </label>
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              setPin(val);
              setError(null);
            }}
            placeholder={t('doc.pinPlaceholder')}
            className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}

          {reasonRequired && (
            <>
              <label className="mt-4 block text-sm font-medium text-gray-700 mb-2">
                {t('ingredients.archive.reasonLabel')}
              </label>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </>
          )}

          <button
            type="submit"
            disabled={pin.length < 4 || (reasonRequired && !reason.trim()) || loading}
            className="w-full mt-4 py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('doc.signing')}
              </>
            ) : (
              submitLabel || t('doc.signDocument')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ModalSignPin;
